import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useOrderDocumentsQuery } from '../api/queries/useOrderDocumentsQuery'
import { TITLE_DEED_POLL_INTERVAL_MS, TITLE_DEED_POLL_WINDOW_MS } from '../constants/orderDocuments'
import {
  fallbackTitleDeedFileName,
  findTitleDeed,
  titleDeedPollDelay,
} from '../utils/orderDocumentUtils'
import type { OrderDocumentFile } from '../types/orderTypes'
import { useOrderDocumentFileQuery } from '../api/queries/useOrderDocumentFileQuery'

/**
 * The title deed row. The documents list loads with the page (it decides
 * whether the row exists at all); the PDF itself only on click.
 *
 * With `shouldPoll`, the list is re-read every 10 s until the certificate
 * appears or a 60 s window — fixed once, when this composable is set up —
 * runs out. The row then appears on its own, no reload.
 */
export function useOrderTitleDeed(
  orderId: MaybeRefOrGetter<string>,
  orderReference: MaybeRefOrGetter<string>,
  shouldPoll: MaybeRefOrGetter<boolean> = false,
) {
  // Set once at mount. Recomputing it per response would keep the poll alive
  // forever for a certificate that never comes.
  const pollDeadline = Date.now() + TITLE_DEED_POLL_WINDOW_MS

  const documentsQuery = useOrderDocumentsQuery(orderId, (documents) =>
    titleDeedPollDelay({
      documents,
      shouldPoll: toValue(shouldPoll),
      deadline: pollDeadline,
      intervalMs: TITLE_DEED_POLL_INTERVAL_MS,
    }),
  )

  const titleDeed = computed(() => findTitleDeed(documentsQuery.data.value ?? []))

  const { file, load: loadFile } = useOrderDocumentFileQuery({
    document: titleDeed,
    fallbackFileName: () => fallbackTitleDeedFileName(toValue(orderReference)),
    missingMessage: 'Title deed not issued yet',
  })

  const load = (): Promise<OrderDocumentFile> => loadFile()

  return {
    isAvailable: computed(() => titleDeed.value !== null),
    /** The public CDN link — the new-tab fallback when a blob fetch is blocked. */
    url: computed(() => titleDeed.value?.url ?? ''),
    sizeBytes: computed(() => titleDeed.value?.sizeBytes ?? file.value?.sizeBytes ?? null),
    load,
  }
}
